import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";

import React from "react";
import ReactDOM from "react-dom";
import App from "./routes/App";
import { ConfirmProvider } from "material-ui-confirm";
import * as Sentry from "@sentry/react";

/**
 * Only built bundles report. `npm start` reads REACT_APP_GIT_SHA straight from
 * .env, where it is a literal `git rev-parse` string that dotenv never executes,
 * so local sessions were filing events against an unusable release. The build
 * pipeline substitutes a real SHA, so staging and production are unaffected.
 */
const isBuiltBundle = process.env.NODE_ENV === "production";

function _isAttributableFrame(frame) {
  return Boolean(frame.filename) && frame.filename !== "<anonymous>";
}

const sentryOptions = {
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_ENV,
  release: process.env.REACT_APP_GIT_SHA,
  enableLogs: true,
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
  ],
  /**
   * Browser extensions and other injected scripts are evaluated without a source
   * file, so the frame that actually threw carries no filename. Those crashes
   * are not ours and cannot be actioned, so drop them rather than let them sit
   * in the issue list looking like product bugs.
   */
  beforeSend(event) {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    const throwingFrame = frames?.[frames.length - 1];

    if (throwingFrame && !_isAttributableFrame(throwingFrame)) {
      return null;
    }

    return event;
  },
};

if (isBuiltBundle) {
  try {
    Sentry.init(sentryOptions);
    const sentryClient = Sentry.getClient();

    if (sentryClient) {
      console.info("Sentry initialized successfully", {
        dsnConfigured: Boolean(sentryOptions.dsn),
        environment: sentryOptions.environment,
        release: sentryOptions.release,
      });
    } else {
      console.warn("Sentry initialized but no client was created");
    }
  } catch (error) {
    console.error("Sentry initialization failed", error);
  }
}

ReactDOM.render(
  <ConfirmProvider>
    <App />
  </ConfirmProvider>,
  document.getElementById("root"),
);
